// Single source of truth for deal age math.
//
// deals.days_in_stage is reset to 0 by track_stage_change() but nothing ever
// increments it, so the stored column is stale on almost every deal. Always
// derive age from stage_entered_at / created_at (both trigger-maintained and
// non-null in practice); days_in_stage survives only as a legacy fallback for
// rows fetched without stage_entered_at.

export interface DealAgeFields {
  stage_entered_at?: string | null;
  phase_entered_at?: string | null;
  days_in_stage?: number | null;
  created_at?: string | null;
}

const MS_PER_DAY = 86_400_000;

/** Whole days elapsed since an ISO timestamp. Null when the input is missing/bad. */
export function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / MS_PER_DAY));
}

/** Days the deal has been in its current stage (live-computed). */
export function daysInStage(d: DealAgeFields): number {
  return daysSince(d.stage_entered_at) ?? Number(d.days_in_stage ?? 0) ?? 0;
}

/** Days the deal has been in its current phase. Null when phase_entered_at wasn't fetched. */
export function daysInPhase(d: DealAgeFields): number | null {
  return daysSince(d.phase_entered_at);
}

/** Days since the deal was created. Null when created_at wasn't fetched. */
export function daysSinceCreated(d: DealAgeFields): number | null {
  return daysSince(d.created_at);
}

/** Select-string fragment every card query should carry for age chips. */
export const DEAL_AGE_COLS = "stage_entered_at,phase_entered_at,created_at";
